import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { alpha, useTheme } from '@mui/material/styles';
import {
  evictReconciliationCache,
  getClientTransactions,
  getReconciliationHistory,
  uploadReconciliation,
  ClientTransactions,
  ReconciliationIssue,
  ReconciliationResult,
} from '../../api/reconciliation';

// ── Source slot definitions ───────────────────────────────────────────────────

const FILE_SLOTS = [
  { id: 'mt5File' as const, label: 'MT5 Report', icon: '📊', hint: 'XLSX' },
  { id: 'mttFile' as const, label: 'MTT Report', icon: '📈', hint: 'CSV' },
  { id: 'mpesaDepositsFile' as const, label: 'MPESA Deposits', icon: '💰', hint: 'XLS' },
  { id: 'mpesaWithdrawalsFile' as const, label: 'MPESA Withdrawals', icon: '💸', hint: 'XLS' },
] as const;

type FileSlotId = typeof FILE_SLOTS[number]['id'];
type FileMap = Record<FileSlotId, File | null>;

function makeEmptyFileMap(): FileMap {
  return { mt5File: null, mttFile: null, mpesaDepositsFile: null, mpesaWithdrawalsFile: null };
}

function normalizeResult(result: ReconciliationResult): ReconciliationResult {
  return { ...result, issues: result.issues ?? [] };
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// ── Formatting helpers ────────────────────────────────────────────────────────

function kes(n: number | undefined): string {
  return `KES ${(n ?? 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StatMetric({
  label,
  value,
  size = 'normal',
  color,
  alert,
}: {
  label: string;
  value: string | number;
  size?: 'normal' | 'large';
  color?: string;
  alert?: boolean;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ display: 'block', color: 'text.secondary', fontWeight: 500, mb: 0.25, lineHeight: 1.3 }}
      >
        {label}
      </Typography>
      <Typography
        variant={size === 'large' ? 'h4' : 'h5'}
        fontWeight={700}
        sx={{ color: alert ? 'error.main' : (color ?? 'text.primary'), lineHeight: 1.1 }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// ── File uploader slot ────────────────────────────────────────────────────────

function FileSlotUploader({
  slot,
  file,
  onFile,
  onRemove,
}: {
  slot: (typeof FILE_SLOTS)[number];
  file: File | null;
  onFile: (id: FileSlotId, file: File) => void;
  onRemove: (id: FileSlotId) => void;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFile = (fileList: FileList | null) => {
    if (!fileList?.length) return;
    onFile(slot.id, fileList[0]);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: 'text.primary' }}>
        {slot.icon} {slot.label}
      </Typography>

      {!file ? (
        <>
          <Box
            onClick={() => inputRef.current?.click()}
            onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files); }}
            onDragOver={(e) => e.preventDefault()}
            sx={{
              border: '1.5px dashed',
              borderColor: 'divider',
              borderRadius: 1.5,
              p: 1.5,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              transition: 'all 0.15s',
            }}
          >
            <UploadFileRoundedIcon sx={{ fontSize: 22, color: 'text.disabled', mb: 0.25 }} />
            <Typography variant="caption" display="block" color="text.secondary">
              Drag and drop file here
            </Typography>
            <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
              {slot.hint}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 0.75, textTransform: 'none', fontSize: '0.75rem', height: 28 }}
            onClick={() => inputRef.current?.click()}
            fullWidth
          >
            Browse files
          </Button>
        </>
      ) : (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            p: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
              <UploadFileRoundedIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}
              >
                {file.name}
              </Typography>
            </Stack>
            <Tooltip title="Remove file">
              <IconButton size="small" onClick={() => onRemove(slot.id)} sx={{ p: 0.25 }}>
                <CloseRoundedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="*"
        style={{ display: 'none' }}
        onChange={(e) => handleFile(e.target.files)}
        onClick={(e) => { (e.target as HTMLInputElement).value = ''; }}
      />
    </Box>
  );
}

// ── Issues table ───────────────────────────────────────────────────────────────

function IssuesTable({ rows, onCrmClick }: { rows: ReconciliationIssue[]; onCrmClick?: (crmId: string) => void }) {
  if (!rows.length) return <Alert severity="success">No issues found for this category.</Alert>;
  return (
    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 420 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {['Issue Type', 'Type', 'CRM ID', 'MT Ticket', 'Full Name', 'Currency', 'MPESA Ref', 'Amount', 'Phone', 'Account', 'Narration', 'Processed At', 'Age (min)'].map((h) => (
              <TableCell key={h} sx={{ fontWeight: 700, bgcolor: 'action.hover', whiteSpace: 'nowrap' }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.slice(0, 500).map((row, i) => (
            <TableRow key={i} hover>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.issueType}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.transactionType}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {row.crmId && onCrmClick ? (
                  <Link component="button" type="button" underline="hover" onClick={() => onCrmClick(row.crmId as string)}>
                    {row.crmId}
                  </Link>
                ) : (
                  row.crmId ?? '—'
                )}
              </TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.mtTicket ?? '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.fullName ?? '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.processedCurrency ?? '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.finalMpesaRef ?? '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.amount != null ? row.amount.toLocaleString() : '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.phone ?? '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.account ?? '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.narration ?? '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.processedAt ?? '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.ageMinutes ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Reconciliation section (deposits or withdrawals) ──────────────────────────

function ReconcSection({
  title,
  icon,
  result,
  type,
  onCrmClick,
}: {
  title: string;
  icon: string;
  result: ReconciliationResult;
  type: 'deposit' | 'withdrawal';
  onCrmClick?: (crmId: string) => void;
}) {
  const prefix = type === 'deposit' ? 'Deposit' : 'Withdrawal';

  const mt5Count = type === 'deposit' ? result.mt5DepositCount : result.mt5WithdrawalCount;
  const mt5Total = type === 'deposit' ? result.mt5DepositTotal : result.mt5WithdrawalTotal;
  const mttCount = type === 'deposit' ? result.mttDepositCount : result.mttWithdrawalCount;
  const mttTotal = type === 'deposit' ? result.mttDepositTotal : result.mttWithdrawalTotal;
  const mpesaCount = type === 'deposit' ? result.mpesaDepositCount : result.mpesaWithdrawalCount;
  const mpesaTotal = type === 'deposit' ? result.mpesaDepositTotal : result.mpesaWithdrawalTotal;
  const crmCount = type === 'deposit' ? result.crmDepositCount : result.crmWithdrawalCount;
  const crmTotal = type === 'deposit' ? result.crmDepositTotal : result.crmWithdrawalTotal;
  const matched = type === 'deposit' ? result.matchedDepositCount : result.matchedWithdrawalCount;
  const unmatched = type === 'deposit' ? result.unmatchedDepositCount : result.unmatchedWithdrawalCount;
  const profitLoss = type === 'deposit' ? result.depositProfitLoss : result.withdrawalProfitLoss;

  const issues = result.issues.filter((i) => i.transactionType === (type === 'deposit' ? 'DEPOSIT' : 'WITHDRAWAL'));

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        {icon} {title}
      </Typography>

      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}><StatMetric label="MT5 Count" value={mt5Count} /></Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}><StatMetric label="MTT Count" value={mttCount} /></Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}><StatMetric label="MPESA Count" value={mpesaCount} /></Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}><StatMetric label="CRM Count" value={crmCount} /></Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}><StatMetric label="Matched" value={matched} /></Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}><StatMetric label="Unmatched" value={unmatched} alert={unmatched > 0} /></Grid>
      </Grid>

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatMetric label="MT5 Total (KES)" value={kes(mt5Total)} size="large" /></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatMetric label="MTT Total (KES)" value={kes(mttTotal)} size="large" /></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatMetric label="MPESA Total (KES)" value={kes(mpesaTotal)} size="large" /></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatMetric label="CRM Total (KES)" value={kes(crmTotal)} size="large" /></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatMetric
            label={`${prefix} Profit / Loss`}
            value={kes(profitLoss)}
            size="large"
            color={profitLoss < 0 ? 'error.main' : profitLoss > 0 ? 'success.main' : undefined}
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        {prefix} Issues ({issues.length})
      </Typography>
      <IssuesTable rows={issues} onCrmClick={onCrmClick} />
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ReconciliationPage() {
  const theme = useTheme();
  const [files, setFiles] = React.useState<FileMap>(makeEmptyFileMap);
  const [depositRate, setDepositRate] = React.useState(130);
  const [withdrawalRate, setWithdrawalRate] = React.useState(130);
  const [reportingDate, setReportingDate] = React.useState(todayIso);
  const [result, setResult] = React.useState<ReconciliationResult | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [snackbar, setSnackbar] = React.useState<string | null>(null);

  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [history, setHistory] = React.useState<ReconciliationResult[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);

  const [evicting, setEvicting] = React.useState(false);

  const [crmSearch, setCrmSearch] = React.useState('');
  const [clientDialogCrmId, setClientDialogCrmId] = React.useState<string | null>(null);
  const [clientLoading, setClientLoading] = React.useState(false);
  const [clientError, setClientError] = React.useState<string | null>(null);
  const [clientData, setClientData] = React.useState<ClientTransactions | null>(null);

  const handleFile = (id: FileSlotId, file: File) => {
    setFiles((prev) => ({ ...prev, [id]: file }));
  };

  const handleRemove = (id: FileSlotId) => {
    setFiles((prev) => ({ ...prev, [id]: null }));
  };

  const filesLoaded = FILE_SLOTS.filter((s) => files[s.id]).length;
  const allFilesLoaded = filesLoaded === FILE_SLOTS.length;

  const handleProcess = async () => {
    if (!allFilesLoaded || !reportingDate) return;
    setLoading(true);
    setError(null);
    try {
      const res = await uploadReconciliation(
        {
          mt5File: files.mt5File as File,
          mttFile: files.mttFile as File,
          mpesaDepositsFile: files.mpesaDepositsFile as File,
          mpesaWithdrawalsFile: files.mpesaWithdrawalsFile as File,
        },
        { depositRate, withdrawalRate },
        reportingDate,
      );
      if (!res.success || !res.data) {
        setError(res.message ?? 'Reconciliation failed.');
        setResult(null);
        return;
      }
      setResult(normalizeResult(res.data));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await getReconciliationHistory();
      if (res.success && res.data) setHistory(res.data);
      else setSnackbar(res.message ?? 'Failed to load history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleEvictCache = async () => {
    if (!result) return;
    setEvicting(true);
    try {
      const res = await evictReconciliationCache(result.reportingDate);
      if (res.success) {
        setSnackbar('Cache evicted for this reporting date.');
        setResult(null);
        setFiles(makeEmptyFileMap());
      } else {
        setSnackbar(res.message ?? 'Failed to evict cache.');
      }
    } finally {
      setEvicting(false);
    }
  };

  const handleCrmClick = async (crmId: string) => {
    setClientDialogCrmId(crmId);
    setClientData(null);
    setClientError(null);
    setClientLoading(true);
    try {
      const res = await getClientTransactions(crmId, result?.reportingDate);
      if (res.status === 410) {
        setClientError('Cached reconciliation has expired for this date — upload the files again.');
      } else if (res.success && res.data) {
        setClientData(res.data);
      } else {
        setClientError(res.message ?? 'Failed to load client transactions.');
      }
    } finally {
      setClientLoading(false);
    }
  };

  const closeClientDialog = () => {
    setClientDialogCrmId(null);
    setClientData(null);
    setClientError(null);
  };

  const openIssues = result?.issues?.length ?? 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        mx: { xs: 0, md: '-32px' },
      }}
    >
      {/* ── Top upload strip ── */}
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          p: 2,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }} flexWrap="wrap" gap={1}>
          <Typography variant="subtitle2" fontWeight={700}>
            Upload reconciliation files
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              size="small"
              startIcon={<HistoryRoundedIcon />}
              onClick={handleOpenHistory}
              sx={{ textTransform: 'none' }}
            >
              History
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {FILE_SLOTS.map((slot) => (
            <Box key={slot.id} sx={{ flex: '1 1 180px', minWidth: 160, maxWidth: 240 }}>
              <FileSlotUploader
                slot={slot}
                file={files[slot.id]}
                onFile={handleFile}
                onRemove={handleRemove}
              />
            </Box>
          ))}

          <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 1, minWidth: 220 }}>
            <TextField
              size="small"
              type="date"
              label="Reporting Date"
              value={reportingDate}
              onChange={(e) => setReportingDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            {[
              { label: 'Deposit Exchange Rate (KES per 1 USD)', value: depositRate, set: setDepositRate },
              { label: 'Withdrawal Exchange Rate (KES per 1 USD)', value: withdrawalRate, set: setWithdrawalRate },
            ].map(({ label, value, set }) => (
              <React.Fragment key={label}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                  {label}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                  <TextField
                    size="small"
                    type="number"
                    value={value}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      set(Number.isNaN(v) ? 0 : v);
                    }}
                    slotProps={{ htmlInput: { step: 0.5, min: 0 } }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton size="small" onClick={() => set((v) => Math.max(1, v - 0.5))}>
                    <RemoveRoundedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => set((v) => v + 0.5)}>
                    <AddRoundedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>
              </React.Fragment>
            ))}

            <Button
              variant="contained"
              onClick={handleProcess}
              disabled={!allFilesLoaded || !reportingDate || depositRate <= 0 || withdrawalRate <= 0 || loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              {loading ? 'Processing…' : '🚀 Process Files'}
            </Button>

            {result && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineRoundedIcon />}
                onClick={handleEvictCache}
                disabled={evicting}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Evict Cache
              </Button>
            )}

            {!allFilesLoaded && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                Upload all 4 files to process
              </Typography>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Box>

      {/* ── Main dashboard ── */}
      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, md: 3 } }}>

        {!result ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 2 }}>
            <Typography variant="h5" fontWeight={700} color="text.secondary">
              🏦 Empire FX Reconciliation
            </Typography>
            <Typography variant="body2" color="text.disabled" textAlign="center" maxWidth={400}>
              Upload MT5, MTT, MPESA Deposits and MPESA Withdrawals files, set the deposit and withdrawal exchange rates and reporting date, then click Process Files.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Title */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
                🏧 Empire FX Reconciliation Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reporting Date: {result.reportingDate} · Deposit Rate: {result.depositRate} · Withdrawal Rate: {result.withdrawalRate} — operational summary of MPESA, CRM, MT5, and MTT reconciliation, including exceptions and unmatched records.
              </Typography>
            </Box>

            {/* Status banner */}
            <Box
              sx={{
                mb: 3,
                p: 1.5,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: openIssues > 0 ? 'warning.main' : 'success.main',
                bgcolor: openIssues > 0
                  ? alpha(theme.palette.warning.main, 0.08)
                  : alpha(theme.palette.success.main, 0.08),
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {openIssues > 0 ? (
                <>
                  <ErrorRoundedIcon color="warning" sx={{ fontSize: 18 }} />
                  <Typography variant="body2" fontWeight={600} color="warning.main">
                    {openIssues} open issue{openIssues !== 1 ? 's' : ''} detected — review the exception tables below
                  </Typography>
                </>
              ) : (
                <>
                  <CheckCircleRoundedIcon color="success" sx={{ fontSize: 18 }} />
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    System Healthy — no unmatched records found
                  </Typography>
                </>
              )}
            </Box>

            {/* Executive Snapshot */}
            <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderLeft: `3px solid ${theme.palette.primary.main}` }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                Executive Snapshot
              </Typography>
              <Grid container spacing={3}>
                {([
                  { label: 'MPESA Deposits', value: result.mpesaDepositCount },
                  { label: 'CRM Deposits', value: result.crmDepositCount },
                  { label: 'MPESA Withdrawals', value: result.mpesaWithdrawalCount },
                  { label: 'CRM Withdrawals', value: result.crmWithdrawalCount },
                  { label: 'MT5 Deposits', value: result.mt5DepositCount },
                  { label: 'MT5 Withdrawals', value: result.mt5WithdrawalCount },
                  { label: 'MTT Deposits', value: result.mttDepositCount },
                  { label: 'MTT Withdrawals', value: result.mttWithdrawalCount },
                ] as { label: string; value: number; alert?: boolean }[]).map((item) => (
                  <Grid key={item.label} size={{ xs: 6, sm: 4, md: 2 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: item.alert ? alpha(theme.palette.error.main, 0.08) : 'action.hover',
                        border: '1px solid',
                        borderColor: item.alert ? 'error.light' : 'divider',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        {item.label}
                      </Typography>
                      <Typography variant="h4" fontWeight={800} color={item.alert ? 'error.main' : 'text.primary'}>
                        {item.value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 2.5 }} />

              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }} flexWrap="wrap">
                <TextField
                  size="small"
                  placeholder="Lookup client by CRM ID"
                  value={crmSearch}
                  onChange={(e) => setCrmSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && crmSearch.trim()) handleCrmClick(crmSearch.trim()); }}
                  sx={{ minWidth: 220 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SearchRoundedIcon />}
                  disabled={!crmSearch.trim()}
                  onClick={() => handleCrmClick(crmSearch.trim())}
                  sx={{ textTransform: 'none' }}
                >
                  Lookup
                </Button>
              </Stack>

              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                All Issues ({result.issues.length})
              </Typography>
              <IssuesTable rows={result.issues} onCrmClick={handleCrmClick} />
            </Paper>

            {/* Deposits section */}
            <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderLeft: `3px solid ${theme.palette.success.main}` }}>
              <ReconcSection title="Deposits Reconciliation" icon="💰" result={result} type="deposit" onCrmClick={handleCrmClick} />
            </Paper>

            {/* Withdrawals section */}
            <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderLeft: `3px solid ${theme.palette.warning.main}` }}>
              <ReconcSection title="Withdrawals Reconciliation" icon="💸" result={result} type="withdrawal" onCrmClick={handleCrmClick} />
            </Paper>
          </>
        )}
      </Box>

      {/* ── History drawer/panel ── */}
      {historyOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: { xs: '100%', sm: 480 },
            bgcolor: 'background.paper',
            borderLeft: '1px solid',
            borderColor: 'divider',
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 6,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={700}>Reconciliation History</Typography>
            <IconButton size="small" onClick={() => setHistoryOpen(false)}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : !history.length ? (
              <Alert severity="info">No reconciliation history found.</Alert>
            ) : (
              <Stack spacing={1.5}>
                {history.map((h) => (
                  <Paper
                    key={h.id}
                    variant="outlined"
                    sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => { setResult(normalizeResult(h)); setHistoryOpen(false); }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>{h.reportingDate}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Deposits: {h.matchedDepositCount}/{h.mt5DepositCount + h.mttDepositCount + h.mpesaDepositCount} matched · Withdrawals: {h.matchedWithdrawalCount}/{h.mt5WithdrawalCount + h.mttWithdrawalCount + h.mpesaWithdrawalCount} matched · {h.issues?.length ?? 0} issues
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        </Box>
      )}

      {/* ── Client transactions dialog ── */}
      <Dialog open={!!clientDialogCrmId} onClose={closeClientDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Client Transactions — {clientDialogCrmId}
          <IconButton size="small" onClick={closeClientDialog}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {clientLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : clientError ? (
            <Alert severity="warning">{clientError}</Alert>
          ) : clientData ? (
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  💰 Deposits ({clientData.deposits.length})
                </Typography>
                <IssuesTable rows={clientData.deposits} />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  💸 Withdrawals ({clientData.withdrawals.length})
                </Typography>
                <IssuesTable rows={clientData.withdrawals} />
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Box>
  );
}
